import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  useTheme,
  TextareaAutosize,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CloudUpload, Link as LinkIcon, Close } from "@mui/icons-material";

const validationSchema = Yup.object({
  judul: Yup.string().required("Judul wajib dipilih"),
  isi: Yup.string().required("Isi wajib diisi"),
  url: Yup.string().url("URL tidak valid").nullable(),
});

const AddNotifikasiModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const formik = useFormik({
    initialValues: {
      judul: "",
      isi: "",
      url: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        judul: values.judul,
        isi: values.isi,
        url: values.url,
        gambar: selectedImage,
      };
      onSubmit(reqData);
      formik.resetForm();
      setSelectedImage(null);
      setImagePreview(null);
      onClose();
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    const fileInput = document.getElementById("gambar-upload");
    if (fileInput) fileInput.value = "";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Tambah Notifikasi</Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2.5}>
            {/* Judul */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Judul Notifikasi"
                name="judul"
                value={formik.values.judul}
                onChange={formik.handleChange}
                error={formik.touched.judul && Boolean(formik.errors.judul)}
                helperText={formik.touched.judul && formik.errors.judul}
              />
            </Grid>

            {/* Isi */}
            <Grid item xs={12}>
              <TextareaAutosize
                minRows={4}
                style={{
                  width: "100%",
                  padding: 12,
                  borderColor:
                    formik.touched.isi && formik.errors.isi
                      ? theme.palette.error.main
                      : theme.palette.grey[300],
                  borderWidth: 1,
                  borderRadius: 8,
                  fontFamily: "Roboto",
                  fontSize: "0.9rem",
                  resize: "vertical",
                }}
                name="isi"
                value={formik.values.isi}
                onChange={formik.handleChange}
                placeholder="Isi Notifikasi"
              />
              {formik.touched.isi && formik.errors.isi && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", ml: 1 }}
                >
                  {formik.errors.isi}
                </Typography>
              )}
            </Grid>

            {/* URL */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link Tambahan (Opsional)"
                name="url"
                value={formik.values.url}
                onChange={formik.handleChange}
                error={formik.touched.url && Boolean(formik.errors.url)}
                helperText={formik.touched.url && formik.errors.url}
                placeholder="https://contoh.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Image Upload */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `2px dashed ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.grey[50],
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main + "08",
                  },
                }}
              >
                {!imagePreview ? (
                  <>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="gambar-upload"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <label
                      htmlFor="gambar-upload"
                      style={{ cursor: "pointer" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CloudUpload
                          sx={{
                            fontSize: 40,
                            color: theme.palette.grey[400],
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Klik untuk upload gambar
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Format: JPG, PNG, GIF (Maks. 5MB)
                        </Typography>
                      </Box>
                    </label>
                  </>
                ) : (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        borderRadius: 1,
                        objectFit: "contain",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: theme.palette.error.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.error.dark,
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {selectedImage?.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{
              borderColor: theme.palette.grey[300],
              color: theme.palette.grey[600],
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              "&:hover": {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.background.alt,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                backgroundColor: theme.palette.secondary.dark,
              },
            }}
          >
            Kirim
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNotifikasiModal;
